/**
 * CR201: Asset Storage Utilities
 * 
 * Handles uploading assets to Supabase Storage and generating public URLs.
 * Replaces storing SVG markup directly in the database.
 */

import { supabase } from "@/integrations/supabase/client";

// Storage bucket name
export const ASSETS_BUCKET = "app-assets";

// Allowed MIME types
export const ALLOWED_MIME_TYPES = [
  "image/svg+xml",
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

export type AllowedMimeType = typeof ALLOWED_MIME_TYPES[number];

// Size limits (CR314: 10MB for raster uploads)
export const MAX_UPLOAD_SIZE_MB = 10;
export const SIZE_LIMITS = {
  "image/svg+xml": 200 * 1024, // 200KB for SVG
  "image/png": MAX_UPLOAD_SIZE_MB * 1024 * 1024,
  "image/jpeg": MAX_UPLOAD_SIZE_MB * 1024 * 1024,
  "image/webp": MAX_UPLOAD_SIZE_MB * 1024 * 1024,
} as const;

// File extension mapping
const MIME_TO_EXT: Record<AllowedMimeType, string> = {
  "image/svg+xml": "svg",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export interface AssetUploadResult {
  success: boolean;
  url?: string;
  path?: string;
  mimeType?: string;
  sizeBytes?: number;
  error?: string;
}

export interface AssetUploadOptions {
  /** Asset ID (used in storage path) */
  assetId: string;
  /** Asset type category (e.g., "starburst", "logo") */
  assetType: string;
  /** File to upload */
  file: File;
}

/**
 * Validate file before upload
 */
export function validateAssetFile(file: File): { valid: boolean; error?: string } {
  // Check MIME type
  if (!ALLOWED_MIME_TYPES.includes(file.type as AllowedMimeType)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.type}. Allowed: SVG, PNG, JPEG, WebP`,
    };
  }

  // Check size limit
  const sizeLimit = SIZE_LIMITS[file.type as AllowedMimeType];
  if (file.size > sizeLimit) {
    const limitKB = Math.round(sizeLimit / 1024);
    return {
      valid: false,
      error: `File too large: ${Math.round(file.size / 1024)}KB. Max for ${file.type}: ${limitKB}KB`,
    };
  }

  return { valid: true };
}

/**
 * Generate a content hash for deduplication
 */
async function generateContentHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
}

/**
 * Upload an asset file to storage
 */
export async function uploadAssetFile(options: AssetUploadOptions): Promise<AssetUploadResult> {
  const { assetId, assetType, file } = options;

  // Validate file
  const validation = validateAssetFile(file);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  try {
    // Generate content hash for cache-friendly naming
    const contentHash = await generateContentHash(file);
    const ext = MIME_TO_EXT[file.type as AllowedMimeType];
    
    // Storage path: {assetType}/{assetId}/{contentHash}.{ext}
    const storagePath = `${assetType}/${assetId}/${contentHash}.${ext}`;

    // Upload to storage (upsert to handle re-uploads)
    const { error: uploadError } = await supabase.storage
      .from(ASSETS_BUCKET)
      .upload(storagePath, file, {
        upsert: true,
        contentType: file.type,
      });

    if (uploadError) {
      console.error("[assetStorage] Upload failed:", uploadError);
      return { success: false, error: uploadError.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(ASSETS_BUCKET)
      .getPublicUrl(storagePath);

    return {
      success: true,
      url: urlData.publicUrl,
      path: storagePath,
      mimeType: file.type,
      sizeBytes: file.size,
    };
  } catch (err) {
    console.error("[assetStorage] Upload exception:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Upload failed",
    };
  }
}

/**
 * Upload SVG content as a file to storage
 * Used for migrating inline SVG markup to storage URLs
 */
export async function uploadSvgContent(
  assetId: string,
  assetType: string,
  svgContent: string
): Promise<AssetUploadResult> {
  // Create a File from SVG content
  const blob = new Blob([svgContent], { type: "image/svg+xml" });
  const file = new File([blob], "asset.svg", { type: "image/svg+xml" });

  return uploadAssetFile({ assetId, assetType, file });
}

/**
 * Delete an asset from storage
 */
export async function deleteAssetFile(storagePath: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from(ASSETS_BUCKET)
      .remove([storagePath]);

    if (error) {
      console.error("[assetStorage] Delete failed:", error);
      return false;
    }

    return true;
  } catch (err) {
    console.error("[assetStorage] Delete exception:", err);
    return false;
  }
}

/**
 * Get the public URL for a storage path
 */
export function getAssetPublicUrl(storagePath: string): string {
  const { data } = supabase.storage
    .from(ASSETS_BUCKET)
    .getPublicUrl(storagePath);
  return data.publicUrl;
}
